import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { directedCastIsPending, storyCharactersWithoutCast } from 'fym-shared/src/music24/directedCastRules';
import {
  DIRECTED_CAST_PICTURES_PER_VIDEO,
  DIRECTED_CHOICES,
  DIRECTED_MAX_CAST,
  DIRECTED_MAX_CAST_PER_SHOT,
} from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { directedActionError, type DirectedActionError } from '../directedErrors';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { useWriteDirectedStylesMutation } from '../directedVideoApi';
import { castMessages as messages } from '../messages/castMessages';
import { directedMessages } from '../messages/directedMessages';
import { DirectedSongFacts } from '../shell/DirectedSongFacts';
import { DirectedStepLayout } from '../shell/DirectedStepLayout';
import { CastAddCard } from './CastAddCard';
import { CastCard } from './CastCard';
import { castErrorMessage } from './castErrorMessages';
import { castNextLock, castNextOf } from './castNext';
import { CastPictureDialog, type CastPictureInitial } from './CastPictureDialog';
import { CAST_UPLOAD_ROLE, canUploadCast, castHasRoom, castSlotsUsed } from './castRules';
import { PendingCastCard, UploadingCastCard } from './CastStateCards';
import { ConsentBox } from './ConsentBox';
import { FailedCastCard } from './FailedCastCard';
import { StoryCharactersPrompt } from './StoryCharactersPrompt';
import { useArtistPictureChoice } from './useArtistPictureChoice';
import { useCastActions } from './useCastActions';
import { useCastPictureRun } from './useCastPictureRun';
import { useCastUpload } from './useCastUpload';
import { boldPart } from '../../common/richTextChildren';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

// A key from the text of the part: a message with several bold parts has no duplicate key (QA skpr2-3).
const bold = boldPart;

/**
 * Step 1, Cast (design directed-video b1 to b4, j1): the photos of the people, places and things that
 * come back from shot to shot. Upload photos or make a picture; name each one and pick its role.
 * The consent box is always there: an upload waits for its tick (review I3), and a cast with an
 * upload cannot go on without it. "Write 3 stories" starts the director (the
 * stories step shows it at work); with a story picked, every forward button says "Go on to the …"
 * (`castNextOf`), and every one waits until each cast member is ready (owner fixes 2026-10-19). The pictures that the AI makes now come from the video, so they
 * stay after a reload.
 */
const CastStepComponent: React.FC = () => {
  const intl = useIntl();
  const { accountId, catalogItemUuid, video, readOnly, openStep, currentScreen } = useDirectedVideoContext();
  const args = useMemo(
    () => ({ accountId, catalogItemUuid, uuid: video.uuid }),
    [accountId, catalogItemUuid, video.uuid]
  );
  const { cast, castPicturesPending: pending, castPicturesLeft } = video;
  const actions = useCastActions(args, cast);
  const uploader = useCastUpload(args);
  const artistPicture = useArtistPictureChoice(args, cast);
  const [isPictureOpen, setIsPictureOpen] = useState(false);
  const [pictureInitial, setPictureInitial] = useState<CastPictureInitial | null>(null);
  const [writeStyles, writingStyles] = useWriteDirectedStylesMutation();
  // "Try again" of a failed picture card (owner report 2026-10-20).
  const retry = useCastPictureRun();
  const [continueError, setContinueError] = useState<DirectedActionError | null>(null);
  const storyCharacters = useMemo(
    () => (video.story?.characters ?? []).filter((character) => character.needsPicture),
    [video.story]
  );
  const missingCharacters = useMemo(() => storyCharactersWithoutCast(video.story, cast), [cast, video.story]);

  const goingUp = uploader.uploads.length + (artistPicture.isAdding ? 1 : 0);
  const used = castSlotsUsed(cast, pending, goingUp);
  const hasRoom = castHasRoom(used);
  const isWorking = video.directorState === 'working';
  const isEmpty = cast.length === 0 && pending.length === 0 && goingUp === 0;
  const error = uploader.error ?? artistPicture.error ?? actions.error ?? retry.error ?? continueError;

  // ONE forward action for the banner and the foot (owner fix 2026-10-19): its label, its target and
  // its lock. It waits until every cast member is ready (made, uploaded and read).
  const castPending = directedCastIsPending(cast, pending, goingUp);
  const next = castNextOf(video, currentScreen);
  const lock = castNextLock({
    next,
    readOnly,
    isBusy: isWorking || actions.isStarting || writingStyles.isLoading,
    castPending,
    consentMissing: actions.consentMissing,
    directorRunsLeft: video.directorRunsLeft,
  });
  const nextLabel =
    next.kind === 'stories'
      ? intl.formatMessage(messages.writeStories, { count: DIRECTED_CHOICES })
      : intl.formatMessage(messages.goOn, { step: next.step });
  const lockNote = lock === 'castPending' ? intl.formatMessage(messages.castNotReady) : null;

  // The story prompt (fix 2026-10-18): a picture for each recurring person of the story.
  const openPicture = (initial: CastPictureInitial | null) => {
    setPictureInitial(initial);
    setIsPictureOpen(true);
  };
  const goNext = async () => {
    setContinueError(null);
    if (lock !== null) {
      return;
    }
    if (next.kind === 'stories') {
      await actions.startStories();
      return;
    }
    try {
      if (next.writeStyles) {
        await writeStyles(args).unwrap();
      }
      openStep(null);
    } catch (caught) {
      console.error('Directed video: the next step was not started', caught);
      setContinueError(directedActionError(caught));
    }
  };

  const uploadLocked = !canUploadCast(actions.consent);
  const addFiles = (files: File[]) => {
    actions.clearError();
    uploader.clearError();
    uploader.upload(files, CAST_UPLOAD_ROLE, actions.consent, DIRECTED_MAX_CAST - used).catch(console.error);
  };
  const addCard = (variant: 'card' | 'empty') => (
    <CastAddCard
      variant={variant}
      isFull={!hasRoom}
      noPicturesLeft={castPicturesLeft <= 0}
      uploadLocked={uploadLocked}
      disabled={readOnly}
      onFiles={addFiles}
      onMakePicture={() => openPicture(null)}
      artistPicture={artistPicture.offer}
      onUseArtistPicture={() => {
        actions.clearError();
        uploader.clearError();
        artistPicture.add().catch(console.error);
      }}
    />
  );

  const side = (
    <DirectedSongFacts>
      <div className={styles.card}>
        <span className={styles.cardEyebrow}>{intl.formatMessage(directedMessages.rolesTitle)}</span>
        <p className={styles.cardText}>{intl.formatMessage(directedMessages.rolesText, { b: bold })}</p>
      </div>
      <div className={styles.card}>
        <span className={styles.cardEyebrow}>{intl.formatMessage(directedMessages.usedTitle)}</span>
        <p className={styles.cardText}>
          {intl.formatMessage(directedMessages.usedText, { count: DIRECTED_MAX_CAST_PER_SHOT })}
        </p>
      </div>
    </DirectedSongFacts>
  );

  return (
    <DirectedStepLayout side={side}>
      <div className={styles.head}>
        <h2>{intl.formatMessage(messages.title)}</h2>
        <p className={`${styles.sub} ${styles.subDesktop}`}>{intl.formatMessage(messages.intro)}</p>
        <p className={`${styles.sub} ${styles.subPhone}`}>{intl.formatMessage(messages.introPhone)}</p>
      </div>

      {video.story !== null && storyCharacters.length > 0 && (
        <StoryCharactersPrompt
          missing={missingCharacters}
          disabled={readOnly || isWorking || writingStyles.isLoading}
          canMake={castPicturesLeft > 0 && hasRoom}
          onMake={(character) =>
            openPicture({ role: character.kind, label: character.name, description: character.description })
          }
          continueLabel={nextLabel}
          continueDisabled={lock !== null}
          continueNote={lockNote}
          onContinue={() => {
            goNext().catch(console.error);
          }}
        />
      )}

      {isEmpty ? (
        addCard('empty')
      ) : (
        <div className={styles.cast}>
          {cast.map((entry) => (
            <CastCard
              key={entry.id}
              entry={entry}
              role={actions.wishes.has(entry.id) ? 'person' : entry.role}
              readOnly={readOnly}
              onLabel={(label) => actions.setLabel(entry.id, label).catch(console.error)}
              onRole={(role) => actions.setRole(entry, role).catch(console.error)}
              onDescription={(description) => actions.setDescription(entry.id, description).catch(console.error)}
              onRemove={() => actions.remove(entry.id).catch(console.error)}
            />
          ))}
          {pending.map((picture) =>
            picture.status === 'failed' ? (
              <FailedCastCard
                key={picture.id}
                picture={picture}
                readOnly={readOnly}
                // The failed card takes no cast place: its new picture needs one.
                canMake={castPicturesLeft > 0 && hasRoom}
                isBusy={retry.isSending}
                onTryAgain={(role) => {
                  actions.clearError();
                  retry
                    .start({
                      role,
                      label: picture.label,
                      description: picture.description ?? '',
                      replacesPictureId: picture.id,
                    })
                    .catch(console.error);
                }}
                onChangeWords={(role) =>
                  openPicture({
                    role,
                    label: picture.label,
                    description: picture.description ?? '',
                    replacesPictureId: picture.id,
                  })
                }
                onRemove={() => actions.remove(picture.id).catch(console.error)}
              />
            ) : (
              <PendingCastCard key={picture.id} picture={picture} />
            )
          )}
          {uploader.uploads.map((item) => (
            <UploadingCastCard key={item.key} name={item.name} />
          ))}
          {artistPicture.isAdding && <UploadingCastCard name={intl.formatMessage(messages.useArtistPicture)} />}
          {addCard('card')}
        </div>
      )}

      <span className={styles.count}>
        {intl.formatMessage(messages.count, {
          count: cast.length,
          max: DIRECTED_MAX_CAST,
          left: Math.max(0, castPicturesLeft),
          total: DIRECTED_CAST_PICTURES_PER_VIDEO,
        })}
      </span>

      <ConsentBox
        checked={actions.consent}
        showError={actions.consentMissing}
        disabled={readOnly}
        onChange={actions.setConsent}
      />
      {error !== null && (
        <p className={styles.error} role="alert">
          {intl.formatMessage(castErrorMessage(error))}
        </p>
      )}

      <div className={styles.foot}>
        {cast.length > 0 && next.kind === 'stories' && (
          <SMLButton
            disabled={lock === 'readOnly' || lock === 'busy' || lock === 'castPending' || video.directorRunsLeft <= 0}
            onClick={() => actions.startWithNoCast().catch(console.error)}
          >
            {intl.formatMessage(messages.noCast)}
          </SMLButton>
        )}
        <span className={`${styles.grow} ${styles.desktopOnly}`} />
        {lockNote !== null ? (
          <span className={styles.lockNote} role="status" data-testid="cast-next-lock">
            {lockNote}
          </span>
        ) : (
          <span className={styles.footNote}>{intl.formatMessage(messages.free)}</span>
        )}
        <SMLButton variant="primary" disabled={lock !== null} onClick={() => goNext().catch(console.error)}>
          {nextLabel}
        </SMLButton>
      </div>

      <CastPictureDialog isOpen={isPictureOpen} initial={pictureInitial} onClose={() => setIsPictureOpen(false)} />
    </DirectedStepLayout>
  );
};

export const CastStep = TypedMemo(CastStepComponent);
